import { queryOptions } from '@tanstack/react-query';
import { FirebaseClient } from './firebaseClient';
import { TicketingOwnerRef } from '@packages/types';

const client = new FirebaseClient();

export const QUERY_KEYS = {
  TICKETS: 'tickets',
  ORDERS: 'orders',
  BUS: 'bus',
  CONTENT: 'content',
  FESTIVAL_EDITIONS: 'festivalEditions',
  EVENTS: 'events',
};

export const appDataQuery = () => {
  return queryOptions({
    queryKey: [QUERY_KEYS.TICKETS, 'appData'],
    queryFn: async () => {
      return client.getAppData();
    },
    staleTime: 1000 * 60,
  });
};
export const ticketTypeQuery = () => {
  return queryOptions({
    queryKey: [QUERY_KEYS.TICKETS, 'ticketTypes'],
    queryFn: async () => {
      return client.getTicketTypes();
    },
    staleTime: 1000 * 60,
  });
};

export const activeFestivalEditionQuery = () => {
  return queryOptions({
    queryKey: [QUERY_KEYS.FESTIVAL_EDITIONS, 'active'],
    queryFn: async () => {
      return client.getActiveFestivalEdition();
    },
    staleTime: 1000 * 60,
  });
};

export const eventBySlugQuery = (slug: string) => {
  return queryOptions({
    queryKey: [QUERY_KEYS.EVENTS, slug],
    queryFn: async () => {
      return client.getEventBySlug(slug);
    },
    staleTime: 1000 * 60,
    enabled: !!slug,
  });
};

export const eventByIdQuery = (eventId: string | null | undefined) => {
  return queryOptions({
    queryKey: [QUERY_KEYS.EVENTS, 'byId', eventId],
    queryFn: async () => {
      if (!eventId) return null;
      return client.getEvent(eventId);
    },
    staleTime: 1000 * 60,
    enabled: !!eventId,
  });
};

export const ownerTicketTypesQuery = (owner: TicketingOwnerRef | null | undefined) => {
  return queryOptions({
    queryKey: [QUERY_KEYS.TICKETS, 'ownerTicketTypes', owner],
    queryFn: async () => {
      if (!owner) return [];
      return client.getOwnerTicketTypes(owner);
    },
    staleTime: 1000 * 60,
    enabled: !!owner,
  });
};

export const getTicketQuery = (id: string) => {
  return queryOptions({
    queryKey: [QUERY_KEYS.TICKETS, id],
    queryFn: async () => {
      return client.getTicket(id);
    },
    staleTime: 1000 * 60,
  });
};

export const getOrderQuery = (id: string | null | undefined) => {
  return queryOptions({
    queryKey: [QUERY_KEYS.ORDERS, id],
    queryFn: async () => {
      if (!id) return null;
      return client.getOrder(id);
    },
    staleTime: 1000 * 60,
    enabled: !!id,
  });
};

export const getHomeQuery = () => {
  return queryOptions({
    queryKey: [QUERY_KEYS.CONTENT, 'home'],
    queryFn: async () => {
      return client.getHome();
    },
    staleTime: 1000 * 60 * 60 * 24,
    gcTime: 1000 * 60 * 60 * 24,
  });
};
export const getAwarenessQuery = () => {
  return queryOptions({
    queryKey: [QUERY_KEYS.CONTENT, 'awareness'],
    queryFn: async () => {
      return client.getAwareness();
    },
    staleTime: 1000 * 60 * 60 * 24,
    gcTime: 1000 * 60 * 60 * 24,
  });
};
export const getProgramQuery = () => {
  return queryOptions({
    queryKey: [QUERY_KEYS.CONTENT, 'program'],
    queryFn: async () => {
      return client.getProgram();
    },
    staleTime: 1000 * 60 * 60 * 24,
    gcTime: 1000 * 60 * 60 * 24,
  });
};

export const getFAQsQuery = () => {
  return queryOptions({
    queryKey: [QUERY_KEYS.CONTENT, 'faqs'],
    queryFn: async () => {
      return client.getFaqs();
    },
    staleTime: 1000 * 60 * 60 * 24,
    gcTime: 1000 * 60 * 60 * 24,
  });
};

export const getBusOrderQuery = (id: string) => {
  return queryOptions({
    queryKey: [QUERY_KEYS.BUS, id],
    queryFn: async () => {
      return client.getBusOrder(id);
    },
    staleTime: 1000 * 60,
  });
};

export const bussesQuery = () => {
  return queryOptions({
    queryKey: [QUERY_KEYS.BUS],
    queryFn: async () => {
      return client.getBusses();
    },
    staleTime: 1000 * 60,
  });
};

export const activeFestivalBusRoutesQuery = (festivalEditionId: string | null | undefined) => {
  return queryOptions({
    queryKey: [QUERY_KEYS.BUS, 'festivalEdition', festivalEditionId],
    queryFn: async () => {
      if (!festivalEditionId) return [];
      return client.getFestivalBusRoutes(festivalEditionId);
    },
    staleTime: 1000 * 60,
    enabled: !!festivalEditionId,
  });
};
